import React, { useState, useMemo, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Typography, IconButton } from '@mui/material';
import toaster from 'react-hot-toast';
import { useParams, useHistory } from 'react-router-dom';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import Tabs from 'components/common/tabsTable/TabsTable';
import { THEME_MESSAGES } from 'utils/constants';
import { isReleaseUpdateActive } from 'api/admin/washboard/washboard';
import { useCancelDeviceFirmwareUpdate } from 'api/queries/useCancelDeviceFirmwareUpdate';
import { useDownloadLogsWashboardDevice } from 'api/queries/useDownloadLogsWashboardDevice';
import { useRebootWashboardDevice } from 'api/queries/useRebootWashboardDevice';
import { useReinitializeWashboardDevice } from 'api/queries/useReinitializeWashboardDevice';
import { useStoresForBusiness } from 'api/queries/useStoresForBusiness';
import { useWashboardDeviceDetails } from 'api/queries/useWashboardDeviceDetails';
import './WashboardDeviceDetails.scss';
import DeviceActivityLogs from './components/DeviceActivityLogs';
import DeviceConfiguration from './components/DeviceConfiguration';
import DeviceScreen from './components/DeviceScreen';
import FirmwareUpdateProgress from './components/FirmwareUpdateProgress';

const WashboardDeviceDetails: React.FC = () => {
  const { id: deviceId } = useParams<{ id: string }>();
  const history = useHistory();
  const [tabValueSelected, setTabValueSelected] = useState(0);
  const [selectedBusinessId, setSelectedBusinessId] = useState<number | null>(null);
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);

  const handleDeviceDetailsError = (error: unknown) => {
    toaster.error((error as any)?.response?.data?.error || THEME_MESSAGES.somethingWrong);
    history.push('/washboard');
  };

  const {
    data: deviceDetails,
    isLoading,
    refetch,
  } = useWashboardDeviceDetails(Number(deviceId), handleDeviceDetailsError);

  const reinitializeMutation = useReinitializeWashboardDevice();
  const rebootMutation = useRebootWashboardDevice();
  const downloadLogsMutation = useDownloadLogsWashboardDevice();
  const cancelFirmwareUpdateMutation = useCancelDeviceFirmwareUpdate();

  const { data: stores = [] } = useStoresForBusiness(
    selectedBusinessId ?? 0,
    Boolean(selectedBusinessId),
  );

  const storeOptions = useMemo(
    () =>
      (stores ?? []).map((store) => ({
        value: store.id,
        display: `${store.name} [ID: ${store.id}]`,
      })),
    [stores],
  );

  useEffect(() => {
    if (deviceDetails?.businessId) {
      setSelectedBusinessId(deviceDetails.businessId);
    }
    if (deviceDetails?.locationId) {
      setSelectedStoreId(deviceDetails.locationId);
    }
  }, [deviceDetails]);

  const formatMachineStatus = (
    machineStatus: string | null,
    machineUpperStatus: string | null,
  ): string => {
    if (!machineStatus && !machineUpperStatus) {
      return 'N/A';
    }

    if (machineUpperStatus) {
      const upper = machineUpperStatus || 'N/A';
      const lower = machineStatus || 'N/A';
      return `U: ${upper} | L: ${lower}`;
    }

    return machineStatus || 'N/A';
  };

  const handleUpdateSuccess = () => {
    refetch();
  };

  const tabsData = useMemo(() => {
    if (!deviceDetails) {
      return [];
    }

    const configurationTab = {
      name: 'Configuration',
      value: 'configuration',
      children: (
        <DeviceConfiguration
          deviceDetails={deviceDetails}
          business={{ id: selectedBusinessId }}
          location={{ id: selectedStoreId, options: storeOptions, businessId: selectedBusinessId }}
          setBusinessId={setSelectedBusinessId}
          setStoreId={setSelectedStoreId}
          deviceId={Number(deviceId)}
          onUpdateSuccess={handleUpdateSuccess}
        />
      ),
    };

    const screenTab = {
      name: 'Screen',
      value: 'screen',
      children: <DeviceScreen deviceId={Number(deviceId)} />,
    };

    const activityLogsTab = {
      name: 'Activity Logs',
      value: 'activityLogs',
      children: <DeviceActivityLogs deviceId={Number(deviceId)} />,
    };

    return [configurationTab, screenTab, activityLogsTab];
  }, [deviceDetails, selectedBusinessId, selectedStoreId, storeOptions, deviceId]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValueSelected(newValue);
  };

  const handleClose = () => {
    history.push('/washboard');
  };

  const handleBusinessNavigate = () => {
    if (deviceDetails?.businessId) {
      history.push(`/businesses/${deviceDetails.businessId}`);
    }
  };

  const handleLocationNavigate = () => {
    if (deviceDetails?.locationId) {
      history.push(`/stores/${deviceDetails.locationId}`);
    }
  };

  const handleReinitialize = async () => {
    await reinitializeMutation.mutateAsync({
      deviceId: Number(deviceId),
      deviceName: deviceDetails!.deviceName,
    });
  };

  const handleReboot = async () => {
    await rebootMutation.mutateAsync({
      deviceId: Number(deviceId),
      deviceName: deviceDetails!.deviceName,
    });
  };

  const hasActiveFirmwareUpdate = useMemo(
    () => isReleaseUpdateActive(deviceDetails?.releaseUpdate?.status),
    [deviceDetails?.releaseUpdate?.status],
  );

  const handleCancelFirmwareUpdate = () => {
    cancelFirmwareUpdateMutation.mutate({ deviceId: Number(deviceId) });
  };

  const handleDownloadLogs = async () => {
    await downloadLogsMutation.mutateAsync({
      deviceId: Number(deviceId),
      deviceName: deviceDetails!.deviceName,
    });
  };

  if (isLoading) {
    return <CentsLoader />;
  }

  return (
    <div className="page-container washboard-device-details">
      <div className="device-header">
        <Typography variant="h5" className="device-mac-address">
          {deviceDetails?.deviceName}
        </Typography>
        <div className="header-actions">
          <Button variant="outlined" disabled>
            Factory Reset
          </Button>
          <Button
            variant="outlined"
            disabled={downloadLogsMutation.isLoading}
            onClick={handleDownloadLogs}
          >
            Download Logs
          </Button>
          <Button variant="outlined" disabled>
            Open Tunnel
          </Button>
          <Button
            variant="outlined"
            disabled={reinitializeMutation.isLoading}
            onClick={handleReinitialize}
          >
            Reinitialize Device
          </Button>
          <Button variant="outlined" disabled={rebootMutation.isLoading} onClick={handleReboot}>
            Reboot Device
          </Button>
          <Button
            variant="outlined"
            color="error"
            disabled={!hasActiveFirmwareUpdate || cancelFirmwareUpdateMutation.isLoading}
            onClick={handleCancelFirmwareUpdate}
          >
            Cancel FW Update
          </Button>
          <IconButton onClick={handleClose} className="close-button">
            <CloseIcon />
          </IconButton>
        </div>
      </div>

      <div className="device-summary-bar">
        <div className="summary-item">
          <span className="summary-label">Device Type</span>
          <span className="summary-value">{deviceDetails?.deviceType ?? 'N/A'}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">System State</span>
          <span className="summary-value">{deviceDetails?.systemStatus ?? 'N/A'}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Machine State</span>
          <span className="summary-value">
            {formatMachineStatus(
              deviceDetails?.machineStatus ?? null,
              deviceDetails?.machineUpperStatus ?? null,
            )}
          </span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Firmware Version</span>
          <FirmwareUpdateProgress
            releaseVersion={deviceDetails?.releaseVersion ?? null}
            packages={deviceDetails?.packages ?? []}
            releaseUpdate={deviceDetails?.releaseUpdate ?? null}
          />
        </div>
        <div className="summary-item">
          <span className="summary-label">Business</span>
          <span
            className={`summary-value${deviceDetails?.businessId ? ' link' : ''}`}
            onClick={handleBusinessNavigate}
            role={deviceDetails?.businessId ? 'button' : undefined}
          >
            {deviceDetails?.businessName}
          </span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Location</span>
          <span
            className={`summary-value${deviceDetails?.locationId ? ' link' : ''}`}
            onClick={handleLocationNavigate}
            role={deviceDetails?.locationId ? 'button' : undefined}
          >
            {deviceDetails?.locationName}
          </span>
        </div>
      </div>

      <div className="device-tabs-container">
        <Tabs
          tabsData={tabsData}
          tabValueSelected={tabValueSelected}
          handleTabChange={handleTabChange}
        />
      </div>
    </div>
  );
};

export default WashboardDeviceDetails;
